import React from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { useResponsive, Flex, Button } from '../../../layouts/MainCompontes/index';

/**
 * ResponsiveSearchBar Component
 * 
 * A fast, fully responsive search bar with action buttons that automatically adapts to all screen sizes.
 * Never overlaps or merges elements.
 * 
 * @param {string} searchValue - Current search input value
 * @param {function} onSearchChange - Callback when search input changes
 * @param {string} searchPlaceholder - Placeholder text for search input
 * @param {Array} actions - Array of action button configs: [{ variant, onClick, icon, text, mobileText, className }]
 * @param {string} searchMaxWidth - Max width for search input on desktop (default: "450px")
 * @param {string} gap - Gap between elements (default: "16px" on desktop, "12px" on mobile)
 * 
 * @example
 * <ResponsiveSearchBar
 *   searchValue={search}
 *   onSearchChange={(e) => setSearch(e.target.value)}
 *   searchPlaceholder="Search employees..."
 *   actions={[
 *     {
 *       variant: "success",
 *       onClick: handleViewActivity,
 *       icon: <i className="fas fa-chart-line"></i>,
 *       text: "View Activity Tracking",
 *       mobileText: "Activity Tracking"
 *     },
 *     {
 *       variant: "primary",
 *       onClick: handleAdd,
 *       icon: "+",
 *       text: "Add Employee"
 *     }
 *   ]}
 * />
 */
const ResponsiveSearchBar = ({
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "Search...",
    actions = [],
    searchMaxWidth = "450px",
    gap = null,
    className = "",
    style = {}
}) => {
    const screen = useResponsive();
    
    // Custom breakpoint: 750px - row layout at 750px and above, column layout below
    const isRowLayout = screen.width >= 750;
    
    // Auto-calculate gap based on screen size
    const calculatedGap = gap || (isRowLayout ? (screen.isTablet ? "12px" : "16px") : "12px");

    return (
        <Flex 
            direction={isRowLayout ? "row" : "column"}
            justify={isRowLayout ? "space-between" : "flex-start"}
            align={isRowLayout ? "center" : "stretch"}
            wrap="wrap"
            gap={calculatedGap}
            className={className}
            style={{ 
                width: "100%", 
                overflow: "hidden",
                boxSizing: "border-box",
                ...style
            }}
        >
            {/* Search Input */}
            <InputGroup 
                className="p-0" 
                style={{ 
                    maxWidth: isRowLayout ? (screen.isTablet ? "100%" : searchMaxWidth) : "100%", 
                    flex: isRowLayout ? (screen.isTablet ? "1 1 100%" : "1 1 0") : "1 1 100%",
                    minWidth: isRowLayout ? (screen.isTablet ? "100%" : "250px") : "100%",
                    flexBasis: isRowLayout ? (screen.isTablet ? "100%" : "auto") : "100%",
                    flexShrink: isRowLayout ? (screen.isTablet ? 0 : 1) : 0
                }}
            >
                <Form.Control
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={onSearchChange}
                    style={{ width: "100%" }}
                />
            </InputGroup>

            {/* Action Buttons */}
            {actions.length > 0 && (
                <Flex 
                    gap={isRowLayout ? "10px" : "12px"} 
                    direction={isRowLayout ? "row" : "column"}
                    wrap="nowrap"
                    style={{ 
                        width: isRowLayout ? (screen.isTablet ? "100%" : "auto") : "100%",
                        flexShrink: 0,
                        flexBasis: isRowLayout ? (screen.isTablet ? "100%" : "auto") : "100%",
                        justifyContent: isRowLayout ? "flex-end" : "stretch",
                        alignItems: "center",
                        maxWidth: isRowLayout ? (screen.isTablet ? "100%" : "none") : "100%",
                        minWidth: isRowLayout ? (screen.isTablet ? "100%" : "auto") : "100%"
                    }}
                >
                    {actions.map((action, index) => {
                        const buttonText = !isRowLayout && action.mobileText 
                            ? action.mobileText 
                            : action.text || "";
                        
                        return (
                            <Button 
                                key={index}
                                variant={action.variant || "primary"} 
                                onClick={action.onClick} 
                                className={isRowLayout ? (screen.isTablet ? "w-100" : action.className || "") : "w-100"}
                                style={{ 
                                    whiteSpace: "nowrap",
                                    minWidth: isRowLayout ? (screen.isTablet ? "100%" : "auto") : "100%",
                                    flexShrink: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    ...(action.style || {})
                                }}
                            >
                                {action.icon && <>{action.icon} </>}
                                {buttonText}
                            </Button>
                        );
                    })}
                </Flex>
            )}
        </Flex>
    );
};

export default ResponsiveSearchBar;

